/**
 * useCalibrationMode Hook (Orchestrator)
 * 
 * Main hook that composes all smaller calibration hooks together.
 * Provides a unified interface for calibration mode management.
 */

import { useState, useEffect, useRef } from 'react';
import type { ICalibrationAvailability } from '../types/calibration';
import { useCalibrationFlags } from './useCalibrationFlags';
import { useCalibrationModeDetection } from './useCalibrationModeDetection';
import { useCalibrationSwitcherState } from './useCalibrationSwitcherState';
import { useCalibrationActions } from './useCalibrationActions';

interface UseCalibrationModeParams {
  calibrationFlagsFromHook: any;
  filterValue: any;
  defaultInputList: any[];
  releaseTime: string;
  gameId: string;
  gameName: string;
  byDevice: boolean;
  isFromDataBrainCalibrationTopCharts: boolean;
  setFilterValue: (v: any) => void;
  updateUrl?: (params: any, config: any, flag: boolean) => void;
  updateFilterDatas: (...args: any[]) => void;
  setKpiVisualMetricValue: (v: string) => void;
  setVisualMetricValue: (v: string) => void;
  setVisualMetricValues: (v: string[]) => void;
  jumpVisualMetricsRef: React.MutableRefObject<string[] | null>;
  // Refs
  userManuallyChangedRegionRef: React.MutableRefObject<boolean>;
  userSegmentChangedRegionRef: React.MutableRefObject<boolean>;
  userSegmentCloseRef: React.MutableRefObject<boolean>;
  restoreDefault: React.MutableRefObject<boolean>;
  specialCascader: React.MutableRefObject<boolean>;
}

interface UseCalibrationModeResult {
  // State
  calibrationFlags: ICalibrationAvailability | null;
  scenario: 'mscience' | 'vgi' | 'both' | 'none';
  isInCalibrationMode: boolean;
  isDataBrainTrueDataMode: string;
  isCalibrationSwitcherDisabled: boolean;

  // Actions
  handleViewCalibrationData: (metric: string) => void;
  switchToCalibrationMode: () => void;
  handleSetRegionUSOnly: () => void;
  updateSwitcherDisabled: (filterValue: any) => void;
  setIsDataBrainTrueDataMode: (v: string) => void;
}

/**
 * Main calibration mode hook
 * 
 * Orchestrates all calibration-related functionality:
 * - Parses calibration flags from API
 * - Detects calibration mode state
 * - Manages switcher button state
 * - Provides action handlers
 * - Auto-switches on page load if needed
 * 
 * @param params - Configuration and dependencies
 * @returns Calibration state and action handlers
 * 
 * @example
 * ```typescript
 * const {
 *   calibrationFlags,
 *   isInCalibrationMode,
 *   isCalibrationSwitcherDisabled,
 *   handleViewCalibrationData,
 *   switchToCalibrationMode,
 * } = useCalibrationMode({
 *   calibrationFlagsFromHook,
 *   filterValue,
 *   // ... other params
 * });
 * ```
 */
export function useCalibrationMode(
  params: UseCalibrationModeParams
): UseCalibrationModeResult {
  const {
    calibrationFlagsFromHook,
    filterValue,
    defaultInputList,
    releaseTime,
    gameId,
    gameName,
    byDevice,
    isFromDataBrainCalibrationTopCharts,
    setFilterValue,
    updateUrl,
    updateFilterDatas,
    setKpiVisualMetricValue,
    setVisualMetricValue,
    setVisualMetricValues,
    jumpVisualMetricsRef,
    userManuallyChangedRegionRef,
    userSegmentChangedRegionRef,
    userSegmentCloseRef,
    restoreDefault,
    specialCascader,
  } = params;

  // State for calibration mode
  const [isDataBrainTrueDataMode, setIsDataBrainTrueDataMode] = useState<string>('false');
  const [isCalibrationMetricsSwitcherDisabled, setIsCalibrationMetricsSwitcherDisabled] =
    useState<boolean>(false);
  
  // Track if auto-switch has been triggered to prevent infinite loop
  const hasAutoSwitchedRef = useRef<boolean>(false);

  // 1. Parse calibration flags
  const { calibrationFlags, scenario } = useCalibrationFlags({
    calibrationFlagsFromHook,
  });

  // 2. Detect calibration mode
  const { isInCalibrationMode } = useCalibrationModeDetection({
    filterValue,
  });

  // 3. Manage switcher button state
  const { isDisabled: isSwitcherDisabled, updateDisabledState } = useCalibrationSwitcherState({
    filterValue,
    defaultInputList,
  });

  // 4. Get action handlers
  const { handleViewCalibrationData, switchToCalibrationMode, handleSetRegionUSOnly } =
    useCalibrationActions({
      calibrationFlags,
      filterValue,
      releaseTime,
      defaultInputList,
      byDevice,
      gameId,
      gameName,
      setFilterValue,
      updateUrl,
      updateFilterDatas,
      setKpiVisualMetricValue,
      setVisualMetricValue,
      setVisualMetricValues,
      jumpVisualMetricsRef,
      setIsDataBrainTrueDataMode,
      setIsCalibrationMetricsSwitcherDisabled,
      userManuallyChangedRegionRef,
      userSegmentChangedRegionRef,
      userSegmentCloseRef,
      restoreDefault,
      specialCascader,
    });

  // 5. Sync calibration mode state with filter state
  useEffect(() => {
    if (isInCalibrationMode) {
      if (isDataBrainTrueDataMode !== 'true') {
        setIsDataBrainTrueDataMode('true');
      }
    } else {
      if (isDataBrainTrueDataMode !== 'false') {
        setIsDataBrainTrueDataMode('false');
      }
    }
  }, [isInCalibrationMode, isDataBrainTrueDataMode]);

  // 6. Sync switcher disabled state
  useEffect(() => {
    setIsCalibrationMetricsSwitcherDisabled(isSwitcherDisabled);
  }, [isSwitcherDisabled]);

  // 7. Auto-switch on page load if coming from calibration page (only once)
  useEffect(() => {
    if (isFromDataBrainCalibrationTopCharts && !hasAutoSwitchedRef.current) {
      console.log('[useCalibrationMode] Auto-switching to calibration mode from TopCharts');
      hasAutoSwitchedRef.current = true;
      switchToCalibrationMode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFromDataBrainCalibrationTopCharts]);

  return {
    // State
    calibrationFlags,
    scenario,
    isInCalibrationMode,
    isDataBrainTrueDataMode,
    isCalibrationSwitcherDisabled: isCalibrationMetricsSwitcherDisabled,

    // Actions
    handleViewCalibrationData,
    switchToCalibrationMode,
    handleSetRegionUSOnly,
    updateSwitcherDisabled: updateDisabledState,
    setIsDataBrainTrueDataMode,
  };
}
